// ─── Claude client (browser side) ────────────────────────────────────────────
// Routes every request through the same-origin /api/claude proxy. The
// Anthropic key lives only as a server-side env var on the hosting platform
// and is never compiled into the bundle.
//
// Two helpers exposed:
//   callClaude(systemPrompt, conversationHistory)
//   callClaudeWithMessages(systemPrompt, messages, maxTokens?)
//
// Both wrap the system prompt in a single text block with
// cache_control: { type: "ephemeral" } so the (large) system prompt is
// reused across turns instead of being billed in full every call.

const PROXY_URL = "/api/claude";
const DEFAULT_MAX_TOKENS = 2048;
const MODEL = "claude-sonnet-4-5";

// Fetch with retry + exponential backoff for rate limits (429)
async function fetchWithRetry(url, options, maxRetries = 3) {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const response = await fetch(url, options);
    if (response.status === 429) {
      const retryAfter = response.headers.get("retry-after");
      const waitMs = retryAfter
        ? parseInt(retryAfter) * 1000
        : Math.min(1000 * Math.pow(2, attempt), 30000);
      console.warn(`Rate limited. Retrying in ${waitMs}ms (attempt ${attempt + 1}/${maxRetries})`);
      await new Promise((r) => setTimeout(r, waitMs));
      continue;
    }
    return response;
  }
  throw new Error("Rate limit exceeded after retries. Please wait a moment and try again.");
}

// Build the cached system block. Anthropic prompt caching requires the system
// field to be an array of content blocks with cache_control on the entry we
// want cached. Keeps the (~tens of KB) system prompt out of the per-turn bill.
function cachedSystem(text) {
  if (!text) return "";
  return [{ type: "text", text, cache_control: { type: "ephemeral" } }];
}

async function postToProxy(payload) {
  const response = await fetchWithRetry(PROXY_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    let err = {};
    try { err = await response.json(); } catch {}
    throw new Error(err?.error?.message ?? `API error ${response.status}`);
  }
  const data = await response.json();
  return data.content?.[0]?.text ?? "";
}

export async function callClaude(systemPrompt, conversationHistory) {
  return postToProxy({
    model: MODEL,
    max_tokens: DEFAULT_MAX_TOKENS,
    system: cachedSystem(systemPrompt),
    messages: conversationHistory,
  });
}

// Like callClaude but accepts a pre-built messages array and custom max_tokens.
// Used by OM extraction (large output) — capped server-side at 4096 to keep
// a poisoned/buggy caller from running up the bill.
export async function callClaudeWithMessages(systemPrompt, messages, maxTokens = DEFAULT_MAX_TOKENS) {
  return postToProxy({
    model: MODEL,
    max_tokens: maxTokens,
    system: cachedSystem(systemPrompt),
    messages,
  });
}
